import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["step", "progressBar", "progressStep", "chip", "stepIndicator"]
    static values = { currentStep: { type: Number, default: 0 }, totalSteps: { type: Number, default: 3 } }

    connect() {
        this.currentStepValue = 0
        this.showStep(0)

        // Prevent Enter key from submitting form prematurely
        this.element.addEventListener("keydown", (e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault()
                if (this.currentStepValue < this.totalStepsValue - 1) {
                    this.next()
                }
            }
        })
    }

    next() {
        if (this.validateStep(this.currentStepValue)) {
            this.currentStepValue++
            this.showStep(this.currentStepValue)
        }
    }

    previous() {
        if (this.currentStepValue > 0) {
            this.currentStepValue--
            this.showStep(this.currentStepValue)
        }
    }

    showStep(index) {
        this.stepTargets.forEach((el, i) => {
            if (i === index) {
                el.classList.remove("hidden")
                // Trigger enter animation
                requestAnimationFrame(() => {
                    el.style.opacity = "0"
                    el.style.transform = "translateY(12px)"
                    requestAnimationFrame(() => {
                        el.style.transition = "opacity 0.4s ease, transform 0.4s ease"
                        el.style.opacity = "1"
                        el.style.transform = "translateY(0)"
                    })
                })
            } else {
                el.classList.add("hidden")
            }
        })
        this.updateProgress(index)
        this.updateStepIndicators(index)
    }

    updateProgress(index) {
        if (this.hasProgressBarTarget) {
            const percentage = ((index + 1) / this.totalStepsValue) * 100
            this.progressBarTarget.style.width = `${percentage}%`
        }
    }

    updateStepIndicators(index) {
        if (!this.hasProgressStepTarget) return

        this.progressStepTargets.forEach((el, i) => {
            const dot = el.querySelector("[data-dot]")
            const label = el.querySelector("[data-label]")
            const connector = el.querySelector("[data-connector]")

            if (i < index) {
                // Completed
                if (dot) {
                    dot.className = "flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-bold transition-all duration-300 shadow-[0_0_12px_rgba(99,102,241,0.4)]"
                    dot.innerHTML = '<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>'
                }
                if (label) label.className = "mt-1.5 text-[10px] font-bold text-indigo-300 uppercase tracking-wider"
                if (connector) connector.className = "flex-1 h-0.5 bg-indigo-600 transition-all duration-500"
            } else if (i === index) {
                // Active
                if (dot) {
                    dot.className = "flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-bold ring-4 ring-indigo-600/20 transition-all duration-300 shadow-[0_0_20px_rgba(99,102,241,0.5)]"
                    dot.textContent = i + 1
                }
                if (label) label.className = "mt-1.5 text-[10px] font-bold text-indigo-300 uppercase tracking-wider"
                if (connector) connector.className = "flex-1 h-0.5 bg-white/10 transition-all duration-500"
            } else {
                // Upcoming
                if (dot) {
                    dot.className = "flex h-8 w-8 items-center justify-center rounded-full bg-white/5 border border-white/10 text-gray-500 text-sm font-bold transition-all duration-300"
                    dot.textContent = i + 1
                }
                if (label) label.className = "mt-1.5 text-[10px] font-medium text-gray-600 uppercase tracking-wider"
                if (connector) connector.className = "flex-1 h-0.5 bg-white/10 transition-all duration-500"
            }
        })
    }

    validateStep(index) {
        const stepEl = this.stepTargets[index]
        const inputs = stepEl.querySelectorAll("input[required], select[required], textarea[required]")
        let valid = true

        inputs.forEach(input => {
            const wrapper = input.closest("[data-field-wrapper]")

            if (!input.value.trim()) {
                valid = false
                input.classList.add("border-red-500/50", "ring-1", "ring-red-500/30")
                input.classList.remove("border-white/10")

                // Show error message if exists
                if (wrapper) {
                    const errorMsg = wrapper.querySelector("[data-error-message]")
                    if (errorMsg) errorMsg.classList.remove("hidden")
                }
            } else {
                input.classList.remove("border-red-500/50", "ring-1", "ring-red-500/30")
                input.classList.add("border-white/10")

                if (wrapper) {
                    const errorMsg = wrapper.querySelector("[data-error-message]")
                    if (errorMsg) errorMsg.classList.add("hidden")
                }
            }

            // Email validation
            if (input.type === "email" && input.value.trim()) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                if (!emailRegex.test(input.value.trim())) {
                    valid = false
                    input.classList.add("border-red-500/50", "ring-1", "ring-red-500/30")
                }
            }

            // Password confirmation match
            if (input.name && input.name.includes("password_confirmation") && input.value.trim()) {
                const passwordField = stepEl.querySelector("input[name*='[password]']:not([name*='confirmation'])")
                if (passwordField && passwordField.value !== input.value) {
                    valid = false
                    input.classList.add("border-red-500/50", "ring-1", "ring-red-500/30")
                }
            }
        })

        if (!valid) {
            // Shake animation on invalid step
            stepEl.style.animation = "none"
            requestAnimationFrame(() => {
                stepEl.style.animation = "shake 0.4s ease-in-out"
            })
        }

        return valid
    }

    // Interest chip toggling (reuses logic from interests_controller)
    toggleChip(event) {
        const chip = event.currentTarget
        const checkbox = chip.querySelector('input[type="checkbox"]')

        if (checkbox) {
            checkbox.checked = !checkbox.checked
            this.updateChipAppearance(chip, checkbox.checked)
        }
    }

    updateChipAppearance(chip, isActive) {
        if (isActive) {
            chip.classList.remove(
                "bg-white/5", "border-white/10", "text-gray-400",
                "hover:bg-white/10", "hover:text-white"
            )
            chip.classList.add(
                "bg-indigo-600/20", "border-indigo-500/50", "text-indigo-300",
                "shadow-[0_0_12px_rgba(99,102,241,0.15)]"
            )
        } else {
            chip.classList.remove(
                "bg-indigo-600/20", "border-indigo-500/50", "text-indigo-300",
                "shadow-[0_0_12px_rgba(99,102,241,0.15)]"
            )
            chip.classList.add(
                "bg-white/5", "border-white/10", "text-gray-400",
                "hover:bg-white/10", "hover:text-white"
            )
        }
    }
}
